import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, MessageSquare, LayoutDashboard, Shield, LogOut, Handshake } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useSiteOpen } from "@/components/site/PrelaunchGate";
import { api } from "@/lib/api";

export const Brand = () => (
  <Link to="/" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} data-testid="nav-brand-logo" className="flex items-center gap-2.5 group">
    <span className="w-9 h-9 rounded-xl bg-qi-pink flex items-center justify-center font-display font-black text-white text-sm group-hover:rotate-6 transition-transform">QI</span>
    <span className="font-display font-black text-white text-lg leading-none tracking-tight">
      FindA<span className="text-qi-pink">Qualified</span>Intermediary
    </span>
  </Link>
);

const linkCls = ({ isActive }) => `text-sm font-semibold transition-colors ${isActive ? "text-white" : "text-white/60 hover:text-white"}`;

const useUnread = (user) => {
  const [u, setU] = useState({ messages: 0, leads: 0 });
  useEffect(() => {
    if (!user) {
      return;
    }
    const load = () => api.get("/chat/unread").then(({ data }) => setU(data)).catch(() => {});
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [user]);
  return u;
};

const UserLinks = ({ user, unread, onLogout }) => (
  <>
    <NavLink to="/dashboard" className={linkCls} data-testid="nav-dashboard-link">
      <span className="inline-flex items-center gap-1.5"><LayoutDashboard size={15} /> Dashboard{unread.leads > 0 && <span className="bg-qi-emerald text-ink text-[10px] font-black rounded-full px-1.5">{unread.leads}</span>}</span>
    </NavLink>
    <NavLink to="/chat" className={linkCls} data-testid="nav-chat-link">
      <span className="inline-flex items-center gap-1.5"><MessageSquare size={15} /> Chat{unread.messages > 0 && <span data-testid="nav-chat-unread" className="bg-qi-pink text-white text-[10px] font-black rounded-full px-1.5">{unread.messages}</span>}</span>
    </NavLink>
    <NavLink to="/partners" className={linkCls} data-testid="nav-partners-link"><span className="inline-flex items-center gap-1.5"><Handshake size={15} /> Partners</span></NavLink>
    {user.role === "admin" && (
      <NavLink to="/admin" className={linkCls} data-testid="nav-admin-link"><span className="inline-flex items-center gap-1.5"><Shield size={15} /> Admin</span></NavLink>
    )}
    <button onClick={onLogout} data-testid="nav-logout-button" className="text-sm font-semibold text-white/50 hover:text-white inline-flex items-center gap-1.5"><LogOut size={15} /> Sign out</button>
  </>
);

export const Navbar = () => {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const unread = useUnread(user);
  const navigate = useNavigate();
  const onLogout = async () => { await logout(); navigate("/"); };
  const siteOpen = useSiteOpen();
  const ctaLabel = siteOpen && user === false ? "List your QI — free" : "Claim a founding spot";

  const links = (
    <>
      {siteOpen && <NavLink to="/directory" className={linkCls} data-testid="nav-directory-link">Find a QI</NavLink>}
      {siteOpen && !user && <a href="/#how" className="text-sm font-semibold text-white/60 hover:text-white" data-testid="nav-how-link">How it works</a>}
      {user ? <UserLinks user={user} unread={unread} onLogout={onLogout} /> : (
        <NavLink to="/login" className={linkCls} data-testid="nav-login-link">Sign in</NavLink>
      )}
    </>
  );

  return (
    <header className="sticky top-0 z-50 bg-ink/80 backdrop-blur-xl border-b border-white/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between gap-6">
        <Brand />
        <nav className="hidden lg:flex items-center gap-7">
          {links}
          {!user && <Link to="/login?mode=register" className="btn-pink !py-2.5 !px-5 text-sm" data-testid="nav-list-qi-button">{ctaLabel}</Link>}
        </nav>
        <button className="lg:hidden text-white" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">{open ? <X /> : <Menu />}</button>
      </div>
      {open && (
        <div className="lg:hidden border-t border-white/10 px-6 py-5 flex flex-col gap-4" onClick={() => setOpen(false)}>
          {links}
          {!user && <Link to="/login?mode=register" className="btn-pink text-sm" data-testid="nav-mobile-list-qi-button">{ctaLabel}</Link>}
        </div>
      )}
    </header>
  );
};
